import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { isMobile, isTablet } from "react-device-detect";
import { toast } from "sonner";
import axios, { AxiosError } from "axios";
// import { useTransition } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import {
  checkoutEmployee,
  createEmployeeActivity,
} from "@/config/mutation/employee";
import { Separator } from "@/components/ui/separator/separator";
import { useJBLogoutModal } from "./use-jb-logout-modal";
import { Textarea } from "@/components/ui/textarea/textarea";
import { WorkAttachments } from "./work-attachments";
import { useAuth } from "@/lib/use-auth";
import { endSession, getSessionToken } from "@/lib/session";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { TodaySoFar } from "./today-so-far";
import { CheckoutSummary } from "./checkout-summary";
import type { DaySummary } from "@/components/break/types";

const formSchema = z.object({
  workDoneToday: z.string(),
  attachments: z
    .array(
      z.object({
        doc: z.string(),
        url: z.string(),
      })
    )
    .optional(),
});

export type FormValues = z.infer<typeof formSchema>;

function getOS() {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = [
    "Macintosh",
    "MacIntel",
    "MacPPC",
    "Mac68K",
    "Darwin",
    "darwin",
  ];
  const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
  const iosPlatforms = ["iPhone", "iPad", "iPod"];
  let os = "Unknown OS";

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "Mac OS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (!os && /Linux/.test(platform)) {
    os = "Linux";
  }

  // console.log("OS", os);
  return os;
}

function getDevice() {
  let deviceType = "Personal Computer";

  if (isMobile) {
    deviceType = "Mobile";
  } else if (isTablet) {
    deviceType = "Tablet";
  }

  // console.log("device", deviceType);
  return deviceType;
}

function getBrowser() {
  const { userAgent } = window.navigator;

  // Determine the browser using user agent
  let browser: string = "";

  if (userAgent.indexOf("Chrome") > -1) {
    browser = "Google Chrome";
  } else if (userAgent.indexOf("Firefox") > -1) {
    browser = "Mozilla Firefox";
  } else if (userAgent.indexOf("Safari") > -1) {
    browser = "Apple Safari";
  } else if (userAgent.indexOf("Edge") > -1) {
    browser = "Microsoft Edge";
  } else if (
    userAgent.indexOf("MSIE") > -1 ||
    !!document["DOCUMENT_NODE"] === true
  ) {
    browser = "Internet Explorer";
  } else {
    browser = "Unknown";
  }

  // console.log("browser", browser);
  return browser;
}

function getScreenSize() {
  const width = window.innerWidth;
  const height = window.innerHeight;

  // console.log(`${width}px X ${height}px`);
  return `${width}px X ${height}px`;
}

const getIpAddress = async () => {
  try {
    const res = await axios.get("https://api.ipify.org/?format=json");
    return res?.data?.ip as string;
  } catch (error) {
    console.log("IP_FETCHING_ERROR", error);
    return "";
  }
};

export const JustBeforeLogoutModal = () => {
  const empKey = getSessionToken() as string;
  const navigate = useNavigate();
  const { data: authData } = useAuth();

  const { open, onOpenChange } = useJBLogoutModal();
  const queryClient = useQueryClient();

  const { mutate: CreateEmployeeActivity } = useMutation({
    mutationFn: createEmployeeActivity(),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workDoneToday: "",
      attachments: [],
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: checkoutEmployee(empKey),
    // mutationFn: checkoutEmployee(""),
  });

  // const checkoutForEmployee = async (
  //   data: {
  //     employee: string;
  //     workDoneToday: string;
  //     attachments: {
  //       doc: string;
  //       url: string;
  //     }[];
  //     ip: string;
  //     device: string;
  //     os: string;
  //     screenSize: string;
  //     browser: string;
  //   },

  //   cb: () => void
  // ) => {
  //   await mutateAsync(data, {
  //     onSuccess: async (response) => {
  //       if (response.success) {
  //         await CreateEmployeeActivity({
  //           employee: authData?.user?._id as string,
  //           mode: "LOGOUT",
  //           ip: data.ip,
  //           device: data.device,
  //           os: data.os,
  //           screenSize: data.screenSize,
  //           browser: data.browser,
  //         });
  //         cb();
  //       } else {
  //         console.log("CHECKOUT_ERROR_1", response);
  //         await mutateAsync(data, {
  //           onSuccess: async (response) => {
  //             if (response.success) {
  //               await CreateEmployeeActivity({
  //                 employee: authData?.user?._id as string,
  //                 mode: "LOGOUT",
  //                 ip: data.ip,
  //                 device: data.device,
  //                 os: data.os,
  //                 screenSize: data.screenSize,
  //                 browser: data.browser,
  //               });
  //               cb();
  //             } else {
  //               console.log("CHECKOUT_ERROR_2", response);
  //               toast.error("Error occured during checkout");
  //             }
  //           },
  //         });
  //       }
  //     },
  //   });
  // };

  const [isPerforming, setIsPerforming] = useState(false);

  // Set once the check-out succeeded; the modal then shows the day summary and
  // the actual logout happens when the employee presses Done (or closes it).
  const [summary, setSummary] = useState<DaySummary | null>(null);

  const finishLogout = () => {
    Cookies.remove("key");
    localStorage.clear();
    // Drop every cached query so a different employee logging in on this tab
    // never sees the previous session's data (break status included).
    queryClient.clear();
    setSummary(null);
    onOpenChange(false);
    toast.success("Log out Success!");
    navigate("/", {
      replace: true,
    });
  };

  // The server no longer accepts this session, so the check-out cannot be
  // saved from here. Say what to do instead of leaving the form stuck on an
  // error: the employee has to sign in again to record it.
  const leaveRejectedSession = () => {
    endSession();
    queryClient.clear();
    onOpenChange(false);
    toast.error(
      "Your session has ended, so your check-out was not saved. Please log in again, then log out to record it.",
      { duration: 15_000 }
    );
    navigate("/login/employee", { replace: true });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && summary) {
      finishLogout();
      return;
    }
    onOpenChange(next);
  };

  const onSubmit: SubmitHandler<FormValues> = async (values: FormValues) => {
    if (values?.workDoneToday === "") {
      return toast.error("Write about what have you done today first!");
    }

    setIsPerforming(true);

    const ip = await getIpAddress();

    const empid = authData?.user?._id as string;

    const mutateData = {
      employee: empid,
      workDoneToday: values?.workDoneToday,
      attachments: values?.attachments ? values?.attachments : [],
      ip,
      device: getDevice(),
      browser: getBrowser(),
      os: getOS(),
      screenSize: getScreenSize(),
    };

    await mutateAsync(mutateData, {
      onSuccess: async (response) => {
        if (response.success) {
          CreateEmployeeActivity({
            employee: empid,
            mode: "LOGOUT",
            ip: ip,
            device: getDevice(),
            browser: getBrowser(),
            os: getOS(),
            screenSize: getScreenSize(),
          });

          setIsPerforming(false);

          if (response.summary) {
            setSummary(response.summary);
          } else {
            finishLogout();
          }
        } else {
          setIsPerforming(false);
          console.log("ERROR WHILE CHECKOUT", response);
          toast.error("ERROR WHILE CHECKOUT");
        }
      },
      onError: (error) => {
        setIsPerforming(false);
        console.log("ERROR WHILE LOGGING OUT AS EMPLOYEE", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError && error.response?.status === 401) {
          leaveRejectedSession();
        } else if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });

    // await checkoutForEmployee(
    //   {
    //     employee: empid,
    //     workDoneToday: values?.workDoneToday,
    //     attachments: values?.attachments ? values?.attachments : [],
    //     ip,
    //     device: getDevice(),
    //     browser: getBrowser(),
    //     os: getOS(),
    //     screenSize: getScreenSize(),
    //   },
    //   () => {
    //     Cookies.remove("key");
    //     localStorage.clear();
    //     setisAuth(false);
    //     onOpenChange(false);
    //     removeUser();
    //     toast.success("Log out Success!");
    //     navigate("/", {
    //       replace: true,
    //     });
    //   }
    // );
  };

  return (
    <div>
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        {/* Scrollable: the day summary makes this taller than a small laptop screen. */}
        <AlertDialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {summary ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle></AlertDialogTitle>
              </AlertDialogHeader>
              <CheckoutSummary summary={summary} onDone={finishLogout} />
            </>
          ) : (
            <AlertDialogHeader>
              <AlertDialogTitle></AlertDialogTitle>
              <TodaySoFar />
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div>
                    <FormField
                      name="workDoneToday"
                      control={form.control}
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <FormLabel>Work done today</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Write here..."
                                rows={4}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <Separator className="my-4 mt-8" />

                    <WorkAttachments isPending={false} />
                  </div>

                  <AlertDialogFooter className="mt-8">
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <Button
                      type="submit"
                      disabled={isPerforming}
                      className="flex items-center gap-2"
                    >
                      {isPerforming && (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      )}
                      Logout
                    </Button>
                  </AlertDialogFooter>
                </form>
              </Form>
            </AlertDialogHeader>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
